import {Color} from '@benchling/nucleus-ui';
import {createStyles, Text} from '@mantine/core';
import React from 'react';

import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';

import type {VaryingInputSection} from '../../procedure-runs/design-tabs/FactorVariationUtils';

interface ProcedureRunDesignFlowchartNodeSectionsProps {
  /** True when the parent node is either archived or pending archive */
  isInert: boolean;
  /** Any varying input sections to be rendered (if omitted, no sections will be rendered) */
  varyingInputSections?: Array<VaryingInputSection>;
}

export default function ProcedureRunDesignFlowchartNodeSections({
  isInert,
  varyingInputSections,
}: ProcedureRunDesignFlowchartNodeSectionsProps): JSX.Element | null {
  const {classes, cx} = useStyles();
  const sections = varyingInputSections ?? [];
  const shouldRenderVaryingInputSections = sections.length > 0;

  if (!shouldRenderVaryingInputSections) {
    return null;
  }

  return (
    <BenchlingNode.Sections spacing={0}>
      {sections.map((section) => (
        <BenchlingNode.Section key={section.title} leftHeader={section.title}>
          {section.rows.map((row, rowIndex) => (
            <BenchlingNode.SectionRow
              key={`${section.title}-${row.label}-${rowIndex}`}
              align="flex-start"
              className={classes.varyingInputRow}
              noWrap={true}
              position="apart"
              spacing="xs"
            >
              <Text
                className={cx(classes.varyingInputLabel, isInert && classes.archivedText)}
                title={row.label}
              >
                {row.label}
              </Text>
              <Text
                className={cx(classes.varyingInputValue, isInert && classes.archivedText)}
                title={row.value || undefined}
              >
                {row.value}
              </Text>
            </BenchlingNode.SectionRow>
          ))}
        </BenchlingNode.Section>
      ))}
    </BenchlingNode.Sections>
  );
}

const useStyles = createStyles((theme) => ({
  archivedText: {
    color: Color.GRAY_600,
  },
  varyingInputRow: {
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.xs,
  },
  varyingInputLabel: {
    flex: 1,
    minWidth: 0,
    color: Color.GRAY_900,
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  varyingInputValue: {
    flexShrink: 0,
    maxWidth: 96,
    color: Color.GRAY_700,
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
    overflow: 'hidden',
    textAlign: 'right',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));
